"use client";

import dynamic from "next/dynamic";
import { useCallback, useState } from "react";
import ImageIcon from "lucide-react/dist/esm/icons/image";
import Box from "lucide-react/dist/esm/icons/box";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { AssetImageGallery } from "@/components/asset-detail/AssetImageGallery";
import type { AssetDetail } from "@/hooks/useAssetDetail";
import { getAssetPreviewModelUrl } from "@/lib/asset-preview-model";
import { getModelType } from "@/components/asset-detail/model-format";
import { logEvent } from "@/lib/events";
import { cn } from "@/lib/utils";

const ModelViewerLazy = dynamic(
  () => import("./ModelViewer").then((m) => m.ModelViewer),
  {
    ssr: false,
    loading: () => (
      <div className="space-y-2">
        <Skeleton className="aspect-[4/3] w-full rounded-xl" />
        <p className="text-xs text-muted-foreground font-mono text-center">Loading viewer…</p>
      </div>
    ),
  }
);

interface AssetMediaStageProps {
  asset: AssetDetail;
  className?: string;
}

export function AssetMediaStage({ asset, className }: AssetMediaStageProps) {
  const modelUrl = getAssetPreviewModelUrl(asset);
  const modelFormat = modelUrl ? getModelType(modelUrl) : null;
  const [tab, setTab] = useState("images");
  const [logged3d, setLogged3d] = useState(false);

  const onTabChange = useCallback(
    (value: string) => {
      setTab(value);
      if (value === "3d" && modelUrl && !logged3d) {
        setLogged3d(true);
        logEvent(
          "asset_preview_opened",
          { preview_type: "3d", asset_slug: asset.slug },
          { targetAssetId: asset.id, severity: "low" }
        );
      }
      logEvent(
        "asset_preview_switched",
        { asset_id: asset.id, preview_type: value as "image" | "3d" | "video" },
        { targetAssetId: asset.id, severity: "low" }
      );
    },
    [asset.id, asset.slug, modelUrl, logged3d]
  );

  // No interactive 3D model for this listing → there's nothing to tab between,
  // so don't render the 3D tab at all; show the image gallery directly.
  if (!modelUrl) {
    return (
      <div className={cn("space-y-3", className)}>
        <AssetImageGallery
          assetId={asset.id}
          images={asset.images}
          videoUrl={asset.video_url}
          title={asset.title}
        />
      </div>
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      <Tabs value={tab} onValueChange={onTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-2 h-10 rounded-lg bg-muted/50 p-1">
          <TabsTrigger
            value="images"
            className="gap-2 rounded-md data-[state=active]:bg-card data-[state=active]:shadow-sm"
          >
            <ImageIcon className="h-4 w-4 shrink-0" aria-hidden />
            Images
          </TabsTrigger>
          <TabsTrigger
            value="3d"
            className="gap-2 rounded-md data-[state=active]:bg-card data-[state=active]:shadow-sm"
            disabled={!modelUrl}
            title={!modelUrl ? "No preview model for this listing" : "3D preview"}
          >
            <Box className="h-4 w-4 shrink-0" aria-hidden />
            3D
          </TabsTrigger>
        </TabsList>

        <TabsContent value="images" className="mt-4 outline-none">
          <AssetImageGallery
            assetId={asset.id}
            images={asset.images}
            videoUrl={asset.video_url}
            title={asset.title}
          />
        </TabsContent>

        <TabsContent value="3d" className="mt-4 outline-none">
          {!modelUrl ? (
            <div className="rounded-xl border border-border bg-card/40 aspect-[4/3] flex flex-col items-center justify-center gap-2 p-6 text-center">
              <Box className="h-10 w-10 text-muted-foreground opacity-50" aria-hidden />
              <p className="text-sm text-muted-foreground max-w-sm">
                No interactive 3D preview for this asset. Use images and the description to evaluate the
                package.
              </p>
            </div>
          ) : tab === "3d" && modelFormat ? (
            <ModelViewerLazy modelUrl={modelUrl} modelType={modelFormat} title="3D preview" />
          ) : tab === "3d" && modelUrl && !modelFormat ? (
            <div className="rounded-xl border border-border bg-card/40 aspect-[4/3] flex flex-col items-center justify-center gap-2 p-6 text-center">
              <Box className="h-10 w-10 text-muted-foreground opacity-50" aria-hidden />
              <p className="text-sm text-muted-foreground max-w-sm">
                Preview URL must end in .glb, .gltf, or .fbx.
              </p>
            </div>
          ) : null}
        </TabsContent>
      </Tabs>
    </div>
  );
}
